import { createContext } from 'react';

// Rollup builds every package entrypoint (`.`, `/contexts`, `/components`,
// `/utils`, …) as an independent bundle with no shared chunks, so a module
// reachable from more than one of them is duplicated — and `createContext()`
// runs once per copy, producing distinct context objects.
//
// That makes legal combinations of public imports fail at runtime: a provider
// from `@1823-partners/core/contexts` with the matching hook from
// `@1823-partners/core` puts the consumer on a different context than the
// provider, so it reads the default value and the hook throws its
// "must be used within…" error. Same for NotificationProvider from one
// entrypoint and PermissionRequestProvider (which calls useNotification
// internally) from the other.
//
// Anchoring the context on the shared global fixes it for every combination,
// the same way the request-access handler registry is anchored (see
// utils/permissionErrors.js).
const SLOT_PREFIX = '__1823PartnersCoreContext_';

// Used only where no global exists (exotic SSR / sandboxed evaluation). Module
// scope, not per call, so repeated calls in such a host still agree with each
// other — it degrades to per-bundle contexts rather than a new one every time.
const localFallback = {};

/**
 * `createContext` that returns the *same* context object across bundles.
 *
 * @param {string} key - Stable name for this context, e.g. 'Notification'.
 * @param {*} [defaultValue=null] - Passed to createContext on first creation.
 * @returns {React.Context}
 */
export const createSharedContext = (key, defaultValue = null) => {
  const host = typeof globalThis !== 'undefined' ? globalThis : localFallback;
  const slot = `${SLOT_PREFIX}${key}__`;
  if (!host[slot]) {
    const context = createContext(defaultValue);
    context.displayName = `1823Core${key}Context`;
    host[slot] = context;
  }
  return host[slot];
};

export default createSharedContext;
